import { IDate, CurrencyEnum, ProductTypeEnum, AgentTypeEnum } from './types';
import { sleep, stringFromIDate, isSameMonth } from '@/utils/index';
import request from '@/utils/request';
import { mockAgentSummariesCache } from './agent';
import { mockProductSummariesCache } from './product';
import { mockInternalNewInvoiceFromAgencySalesReports } from './invoice';

// data type
export namespace search_agency_sales_reports {
  export interface ReportSummary {
    id: string;
    targetMonth: IDate;
    agentId: string;
    agentName: string;
    isFixed: boolean;
  }

  export interface Request {
    targetMonth: IDate;
    agentIds: string[];
  }

  export interface Response {
    reports: ReportSummary[];
  }
}

export namespace get_agency_sales_report_detail {
  export interface Request {
    reportId: string;
  }

  export interface Inventory {
    arrivalAmount: number;
    soldAmount: number;
    refundedAmount: number;
    netSoldAmount: number;
    carryOverAmount: number;
    stockAmount: number;
  }

  export interface Settlement {
    currency: string;
    sales: number;
    refund: number;
    salesCommission: number;
    refundCommission: number;
    total: number;
  }

  export interface Product {
    productId: string;
    productName: string;
    inventory: Inventory;
    settlement: Settlement;
  }

  export interface Response {
    id: string;
    agentId: string;
    agentName: string;
    signedBy: string;
    targetMonth: IDate;
    products: Product[];
    reportedAt: IDate;
    note?: string;
    signedAt: string;
    isFixed: boolean;
    version: string;
  }
}

export namespace put_agency_sales_report {
  export interface ReportedProduct {
    productId: string;
    soldAmount: number;
    refundedAmount: number;
  }

  export interface Request {
    id?: string;
    agentId: string;
    signedBy: string;
    targetMonth: IDate;
    products: ReportedProduct[];
    reportedAt: IDate;
    note?: string;
    version?: string;
  }

  export interface Response {
    reportId: string;
  }
}

export namespace calculate_agency_inventory_sales {
  export interface ReportedProduct {
    productId: string;
    soldAmount: number;
    refundedAmount: number;
  }

  export interface Inventory {
    arrivalAmount: number;
    soldAmount: number;
    refundedAmount: number;
    netSoldAmount: number;
    carryOverAmount: number;
    stockAmount: number;
  }

  export interface Settlement {
    currency: string;
    sales: number;
    refund: number;
    salesCommission: number;
    refundCommission: number;
    total: number;
  }

  export interface Product {
    productId: string;
    productName: string;
    inventory: Inventory;
    settlement: Settlement;
  }

  export interface Request {
    agentId: string;
    targetMonth: IDate;
    products: ReportedProduct[];
  }

  export interface Response {
    products: Product[];
  }
}

// mock
const useMock = process.env.VUE_APP_API_USE_MOCK === 'true';

const mockAgencySalesReportCount = 20;

const mockAgencySalesReportId = (targetMonth: IDate, agentId: string) =>
  `${targetMonth.year}:${
    targetMonth.month >= 10 ? targetMonth.month : '0' + targetMonth.month
  }:${agentId}`;

const mockSettelment = () => ({
  currency: CurrencyEnum.JPY,
  sales: 6000,
  refund: 1000,
  salesCommission: 810,
  refundCommission: 365,
  total: 3825,
});

const generateInvoiceFromAgencySalesReports = (
  reports: Map<string, get_agency_sales_report_detail.Response>,
  targetMonth: IDate
) => {
  const lastMonthReportsByAgent: Map<
    string,
    get_agency_sales_report_detail.Response[]
  > = new Map();
  reports.forEach(r => {
    if (!isSameMonth(r.targetMonth, targetMonth)) {
      return;
    }
    let details = lastMonthReportsByAgent.get(r.agentId);
    if (!details) {
      details = [];
      lastMonthReportsByAgent.set(r.agentId, details);
      details.push(r);
    }
  });
  lastMonthReportsByAgent.forEach(details =>
    mockInternalNewInvoiceFromAgencySalesReports(details, new Date())
  );
};

const mockInitialAgencySalesReportDetailCache = () => {
  const details: Map<
    string,
    get_agency_sales_report_detail.Response
  > = new Map();
  const agents = Array.from(mockAgentSummariesCache.values()).filter(
    a => (a.type as AgentTypeEnum) === AgentTypeEnum.DOMESTIC_AGENCY
  );
  const agentCount = agents.length;
  const products = Array.from(mockProductSummariesCache.values()).filter(
    p => (p.type as ProductTypeEnum) === ProductTypeEnum.REAL_TICKET
  );
  const productCount = products.length;
  const today = new Date();
  const thisMonth = {
    year: today.getFullYear(),
    month: today.getMonth() + 1,
    day: 1,
  } as IDate;
  const lastMonth = {
    year: today.getFullYear(),
    month: today.getMonth(),
    day: 1,
  } as IDate;

  for (let i = 0; i < mockAgencySalesReportCount; i++) {
    const targetMonth = i % 2 === 0 ? lastMonth : thisMonth;
    const agentId = agents[i % agentCount].id;
    const detail: get_agency_sales_report_detail.Response = {
      id: mockAgencySalesReportId(targetMonth, agentId),
      agentId: agentId,
      agentName: agents[i % agentCount].name,
      signedBy: `dummySignedBy${i}`,
      targetMonth: targetMonth,
      products: [
        {
          productId: products[i % productCount].id,
          productName: products[i % productCount].name,
          inventory: {
            arrivalAmount: 10,
            soldAmount: 6,
            refundedAmount: 1,
            netSoldAmount: 5,
            carryOverAmount: 0,
            stockAmount: 5,
          },
          settlement: mockSettelment(),
        },
      ],
      reportedAt: targetMonth,
      signedAt: new Date(stringFromIDate(targetMonth)!).toLocaleString(),
      isFixed: false,
      version: `${new Date().getTime()}`,
    };
    i % 4 < 2 && (detail.note = `dummyNote${i}`);
    details.set(detail.id, detail);
  }

  // generate invoice for last month from agency sales report
  generateInvoiceFromAgencySalesReports(details, lastMonth);

  return details;
};
const mockAgencySalesReportDetailCache = mockInitialAgencySalesReportDetailCache();

const mockSearchAgencySalesReports = (
  req: search_agency_sales_reports.Request
) => {
  const reports: search_agency_sales_reports.ReportSummary[] = [];
  mockAgencySalesReportDetailCache.forEach(r => {
    if (
      req.targetMonth.year === r.targetMonth.year &&
      req.targetMonth.month === r.targetMonth.month &&
      req.agentIds.includes(r.agentId)
    ) {
      reports.push({
        id: r.id,
        targetMonth: r.targetMonth,
        agentId: r.agentId,
        agentName: r.agentName,
        isFixed: false,
      });
    }
  });
  return {
    reports: reports,
  } as search_agency_sales_reports.Response;
};

const mockGetAgencySalesReportDetail = (
  req: get_agency_sales_report_detail.Request
) => {
  return mockAgencySalesReportDetailCache.get(
    req.reportId
  ) as get_agency_sales_report_detail.Response;
};

const mockPutAgencySalesReport = (req: put_agency_sales_report.Request) => {
  const now = new Date();
  const id = mockAgencySalesReportId(req.targetMonth, req.agentId);

  // validation
  if (req.version !== void 0) {
    // update agency sales report
    const report = mockAgencySalesReportDetailCache.get(id);
    if (report) {
      if (report.version !== req.version) {
        throw new Error(
          `mock version not matched, req: ${req.version}, actual: ${report.version}`
        );
      }
    } else {
      throw new Error(
        `no report while trying to update id:${id}, version:${req.version}`
      );
    }
  } else {
    // create agency sales report
    if (mockAgencySalesReportDetailCache.has(id)) {
      throw new Error(`duplicated report while trying to create id:${id}`);
    }
  }

  const products: get_agency_sales_report_detail.Product[] = mockCalculateAgencyInventorySales(
    req
  ).products;
  const res: get_agency_sales_report_detail.Response = {
    id: id,
    agentId: req.agentId,
    agentName: mockAgentSummariesCache.get(req.agentId)!.name,
    signedBy: req.signedBy,
    targetMonth: req.targetMonth,
    products: products,
    reportedAt: req.reportedAt,
    signedAt: now.toLocaleString(),
    isFixed: false,
    version: req.version !== void 0 ? req.version : `${now.getTime()}`,
  };
  if (req.note) {
    res.note = req.note;
  }
  mockAgencySalesReportDetailCache.set(res.id, res);
  return {
    reportId: res.id,
  } as put_agency_sales_report.Response;
};

const mockCalculateAgencyInventorySales = (
  req: calculate_agency_inventory_sales.Request
) => {
  const products: calculate_agency_inventory_sales.Product[] = [];
  req.products.forEach(p => {
    products.push({
      productId: p.productId,
      productName: mockProductSummariesCache.get(p.productId)!.name,
      inventory: {
        arrivalAmount: p.soldAmount + 1,
        soldAmount: p.soldAmount,
        refundedAmount: p.refundedAmount,
        netSoldAmount: p.soldAmount - p.refundedAmount,
        carryOverAmount: 0,
        stockAmount: p.refundedAmount + 1,
      },
      settlement: mockSettelment(),
    });
  });
  return {
    products: products,
  } as calculate_agency_inventory_sales.Response;
};

// api
export const searchAgencySalesReports = async (
  req: search_agency_sales_reports.Request
) => {
  if (useMock) {
    await sleep(500);
    return mockSearchAgencySalesReports(req);
  }

  const res = await request({
    url: '/v1/search-agency-sales-reports',
    method: 'post',
    data: req,
  });
  return res.data as search_agency_sales_reports.Response;
};

export const getAgencySalesReportDetail = async (
  req: get_agency_sales_report_detail.Request
) => {
  if (useMock) {
    await sleep(500);
    return mockGetAgencySalesReportDetail(req);
  }

  const res = await request({
    url: '/v1/get-agency-sales-report-detail',
    method: 'post',
    data: req,
  });
  return res.data as get_agency_sales_report_detail.Response;
};

export const putAgencySalesReport = async (
  req: put_agency_sales_report.Request
) => {
  if (useMock) {
    await sleep(500);
    return mockPutAgencySalesReport(req);
  }

  const res = await request({
    url: '/v1/put-agency-sales-report',
    method: 'post',
    data: req,
  });
  return res.data as put_agency_sales_report.Response;
};

export const calculateAgencyInventorySales = async (
  req: calculate_agency_inventory_sales.Request
) => {
  if (useMock) {
    await sleep(500);
    return mockCalculateAgencyInventorySales(req);
  }

  const res = await request({
    url: '/v1/calculate-agency-inventory-sales',
    method: 'post',
    data: req,
  });
  return res.data as calculate_agency_inventory_sales.Response;
};
